import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { TreeDeciduous, Zap, History, Shield, Activity, Search } from 'lucide-react';
import { MathService } from '../services/mathService';

export const PhysicalWitness: React.FC = () => {
  const [targetYear, setTargetYear] = useState(770);
  const [timelineData, setTimelineData] = useState<any>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [reconstructionProgress, setReconstructionProgress] = useState(0);

  const runReconstruction = () => {
    setIsScanning(true);
    setReconstructionProgress(0);
    
    const progressInterval = setInterval(() => {
      setReconstructionProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setIsScanning(false);
          const data = MathService.generateLatticeTimeline(targetYear);
          setTimelineData(data);
          return 100;
        }
        return prev + 5;
      });
    }, 50);
  };

  useEffect(() => {
    runReconstruction();
  }, [targetYear]);

  return (
    <div id="physical-witness" className="glass-panel p-10 relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-8">
        <TreeDeciduous className="w-16 h-16 text-lattice-cyan/10" />
      </div>

      <div className="mb-12">
        <div className="flex items-center gap-3 mb-2">
          <History className="w-4 h-4 text-lattice-cyan" />
          <h3 className="text-[10px] font-mono font-bold tracking-[0.4em] text-lattice-cyan uppercase">
            Biological Verification
          </h3>
        </div>
        <h2 className="text-4xl font-display font-bold text-white tracking-tighter">
          The <span className="text-lattice-cyan italic">Biological Witness</span>
        </h2>
        <p className="text-xs font-mono text-white/40 uppercase tracking-widest mt-2">
          Dendrochronological Mapping: 14,350 BP to Present
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-8">
          <div className="p-6 bg-white/[0.02] border border-white/10 rounded-sm">
            <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest mb-6">Anchor Parameters</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-white/40 uppercase">Absolute Anchor (BP)</span>
                <span className="text-sm font-mono text-white font-bold">14,350</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-white/40 uppercase">Miyake Intensity</span>
                <span className="text-sm font-mono text-lattice-cyan font-bold">+1.0 Magnitude</span>
              </div>
              <div className="flex flex-col gap-2 pt-4 border-t border-white/5">
                <label className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Target Year (BP)</label>
                <div className="flex items-center gap-4">
                  <input 
                    type="range"
                    min="0"
                    max="14000"
                    step="10"
                    value={targetYear}
                    onChange={(e) => setTargetYear(parseInt(e.target.value))}
                    className="flex-1 accent-lattice-cyan"
                  />
                  <span className="text-xl font-display font-bold text-lattice-cyan w-20 text-right">{targetYear}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-lattice-cyan/5 border border-lattice-cyan/10 rounded-sm">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-lattice-cyan" />
              <h4 className="text-xs font-mono font-bold text-lattice-cyan uppercase tracking-widest">Logic Proof</h4>
            </div>
            <p className="text-[10px] font-mono text-white/60 leading-relaxed">
              "The tree ring IS the subject. The year/solar flux IS the interaction. 
              If growth is 0, the tree still exists (Persistence: Base * 0 = Base). 
              If growth occurs, it is an interaction (Expansion: Base * 1 = Base + 1)."
            </p>
          </div>
        </div>

        <div className="relative">
          {isScanning && (
            <div className="absolute inset-0 z-20 bg-black/40 flex flex-col items-center justify-center backdrop-blur-sm">
              <Activity className="w-8 h-8 text-lattice-cyan animate-pulse mb-4" />
              <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-lattice-cyan"
                  initial={{ width: 0 }}
                  animate={{ width: `${reconstructionProgress}%` }}
                />
              </div>
              <span className="text-[8px] font-mono text-lattice-cyan mt-2 uppercase tracking-[0.2em]">Reconstructing Timeline...</span>
            </div>
          )}

          <div className="h-[400px] bg-black/40 border border-white/5 rounded-sm p-6 overflow-y-auto custom-scrollbar">
            <div className="space-y-4">
              <AnimatePresence mode='popLayout'>
                {timelineData?.events.map((ev: any, idx: number) => (
                  <motion.div 
                    key={ev.year}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-start gap-4 p-4 bg-white/[0.03] border-l-2 border-lattice-cyan"
                  >
                    <div className="shrink-0 pt-1">
                      <Zap className="w-3 h-3 text-lattice-cyan" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono font-bold text-white">YEAR {ev.year} BP</span>
                        <span className="text-[8px] font-mono text-lattice-cyan bg-lattice-cyan/10 px-1 rounded uppercase">EVENT</span>
                      </div>
                      <h5 className="text-xs font-display font-bold text-white uppercase tracking-tight">{ev.event}</h5>
                      <div className="mt-2 text-[10px] font-mono text-white/40">
                        PRESENCE MAGNITUDE: <span className="text-white">{ev.presence.toLocaleString()}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {!isScanning && timelineData && (
                <div className="mt-8 pt-8 border-t border-white/10 text-center">
                  <div className="text-[8px] font-mono text-white/20 uppercase tracking-[0.3em] mb-2">Final Presence Calculation</div>
                  <div className="text-5xl font-display font-bold text-lattice-cyan italic tracking-tighter">
                    {timelineData.presence.toLocaleString()}
                  </div>
                  <p className="text-[10px] font-mono text-white/40 mt-4 uppercase tracking-widest leading-relaxed">
                    Verified against Miyake pulse at 14,350 BP. Zero drift detected in the annual ring record.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-12 flex items-center justify-between p-6 border border-white/5 bg-white/[0.01]">
        <div className="flex items-center gap-4">
          <Search className="w-5 h-5 text-white/20" />
          <span className="text-[8px] font-mono text-white/30 uppercase tracking-[0.2em]">Source: paidingattention.org/dendro-witness</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]" />
          <span className="text-[9px] font-mono text-green-500 font-bold uppercase tracking-widest">Physical Accuracy: 100%</span>
        </div>
      </div>
    </div>
  );
};
